import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Record, Map, Set, OrderedSet } from 'immutable';

import { AppMetadataEntity } from '../../ui/components/app-metadata/app-metadata.entity';
import { ExportCsvConfigurationEntity } from '../components/export-csv-configuration/export-csv-configuration.entity';
import { ExportCharsetEntity } from '../components/export-template-details/export-charset.entity';
import { ExportConnectorEntity } from '../components/export-template-details/export-connector.entity';
import {
	ExportFormat,
	ExportTemplateDetailsEntity,
} from '../components/export-template-details/export-template-details.entity';
import { ExportTemplateListEntity } from '../components/export-template-list/export-template-list.entity';

export interface IExportTemplateState {
	loading: boolean;
	exportInProgress: boolean;
	selectedDatablockLabel: string;
	selectedDatablock: number | undefined;
	metadata: AppMetadataEntity;
	templateDetails: ExportTemplateDetailsEntity;
	connectors: Map<number, ExportConnectorEntity>;
	charsets: Set<ExportCharsetEntity>;
	exportTemplateList: ExportTemplateListEntity;
	predictedNormalizedLabel: string;
}

const defaults: IExportTemplateState = {
	loading: false,
	exportInProgress: false,
	selectedDatablockLabel: '',
	selectedDatablock: undefined,
	metadata: AppMetadataEntity.build(),
	templateDetails: new ExportTemplateDetailsEntity(),
	connectors: Map(),
	charsets: Set(),
	exportTemplateList: ExportTemplateListEntity.build([]),
	predictedNormalizedLabel: '',
};

export const ExportTemplateFeatureKey = 'exportTemplate';

export class ExportTemplateState extends Record<IExportTemplateState>(
	defaults
) {
	public static key = ExportTemplateFeatureKey;

	public reset(): ExportTemplateState {
		return this.setExportTemplateConfig(
			ExportTemplateDetailsEntity.build({
				connector: ExportConnectorEntity.localConnector(),
				outputType: ExportFormat.CSV,
				config: ExportCsvConfigurationEntity.build(),
			})
		)
			.setExportMetadata(AppMetadataEntity.build({}))
			.setEmptyList()
			.setDatablockId(undefined)
			.setDatablockLabel('');
	}

	public setLoading(isLoading: boolean): ExportTemplateState {
		return this.set('loading', isLoading);
	}

	public setExportTemplateConfig(
		config: ExportTemplateDetailsEntity
	): ExportTemplateState {
		return this.set('templateDetails', config);
	}

	public setExportMetadata(metadata: AppMetadataEntity): ExportTemplateState {
		return this.set('metadata', metadata);
	}

	public setAvailableConnectors(
		connectors: Map<number, ExportConnectorEntity>
	): ExportTemplateState {
		return this.set('connectors', connectors);
	}

	public setAvailableCharsets(
		charsets: Set<ExportCharsetEntity>
	): ExportTemplateState {
		return this.set('charsets', charsets);
	}

	public setExportTemplateList(
		list: ExportTemplateListEntity
	): ExportTemplateState {
		return this.set('exportTemplateList', list);
	}

	public setEmptyList(): ExportTemplateState {
		return this.set('exportTemplateList', ExportTemplateListEntity.build([]));
	}

	public setExportInProgress(inProgress: boolean): ExportTemplateState {
		return this.set('exportInProgress', inProgress);
	}

	public setDatablockLabel(
		selectedDatablockLabel: string
	): ExportTemplateState {
		return this.set('selectedDatablockLabel', selectedDatablockLabel);
	}

	public setDatablockId(datablockId: number | undefined): ExportTemplateState {
		return this.set('selectedDatablock', datablockId);
	}

	public setPredictedNormalizedLabel(label: string): ExportTemplateState {
		return this.set('predictedNormalizedLabel', label);
	}
}
